import { useState } from "react"
import { router } from "@inertiajs/react"

export interface Variant {
  id: number
  label: string
  available_stock: number
}

interface VariantSelectorProps {
  variants: Variant[]
  addToCartUrl: string
  labels: {
    size: string
    add_to_cart: string
    in_stock_template: string
    last_one: string
    out_of_stock: string
  }
}

export default function VariantSelector({
  variants,
  addToCartUrl,
  labels,
}: VariantSelectorProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const selected = variants.find((v) => v.id === selectedId) ?? null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selected) return

    router.post(
      addToCartUrl,
      { variant_id: selected.id },
      { preserveScroll: true, preserveState: true }
    )
  }

  const stockMessage = (() => {
    if (!selected) return ""
    if (selected.available_stock <= 0) return labels.out_of_stock
    if (selected.available_stock === 1) return labels.last_one
    return labels.in_stock_template.replace("{count}", String(selected.available_stock))
  })()

  return (
    <div className="mt-6">
      <h3 className="text-sm font-medium text-gray-900 mb-4">{labels.size}</h3>
      <div className="flex flex-wrap gap-3">
        {variants.map((variant) => {
          const available = variant.available_stock > 0
          const isSelected = selectedId === variant.id
          return (
            <label
              key={variant.id}
              className={`relative ${
                available ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              }`}
            >
              <input
                type="radio"
                name="variant_id"
                value={variant.id}
                disabled={!available}
                checked={isSelected}
                onChange={() => setSelectedId(variant.id)}
                className="peer sr-only"
              />
              <span
                className={`flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-900 rounded-md border-2 transition-colors duration-200 ${
                  isSelected ? "border-gray-900" : "border-gray-200"
                } ${available ? "hover:bg-gray-100" : "line-through"}`}
              >
                {variant.label}
              </span>
            </label>
          )
        })}
      </div>

      <form onSubmit={handleSubmit} className="mt-6">
        <button
          type="submit"
          disabled={!selected || selected.available_stock <= 0}
          className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {labels.add_to_cart}
        </button>
        {stockMessage && (
          <p
            className={`text-sm mt-2 ${
              selected && selected.available_stock <= 0
                ? "text-red-500"
                : "text-midgray"
            }`}
          >
            {stockMessage}
          </p>
        )}
      </form>
    </div>
  )
}
